/**
 * Incident Capture Dialog
 *
 * Modal form for reporting a practice incident. Launched from the
 * Event_Incidents_Page when a coach or admin taps "Report Incident".
 *
 * Behaviour (in brief):
 * - Rider selector populated from the passed-in `riders` array and sorted
 *   Students first (anyone not typed 'Coach'), then Coaches, alphabetical by
 *   `formatRiderName(rider)` within each group.
 * - Incident Type is a native `<select>` with a fixed set of three values
 *   (`Crash` | `Mechanical` | `Other`) and defaults to `Crash` on mount.
 * - Description is a multi-line textarea (rows=4) with 1–5000 character
 *   validation against the trimmed value, mirroring the server-side check.
 * - Save is disabled until the form is valid AND any in-flight POST has
 *   resolved. On 201 the dialog fires a success toast (rider + incident type),
 *   invokes `onSuccess`, and closes. On non-2xx the server-provided `error`
 *   text is surfaced inline; on a pure network failure a user-facing
 *   "Could not send submission" message is shown. In both failure modes the
 *   dialog stays open and Save is re-enabled.
 * - Cancel closes the dialog, resets form state, and makes NO network call.
 *
 * Mobile-first / a11y (Req 3.7, 4.6, 7.1, 7.2, 7.3, 7.4, 18.16):
 * - Full-screen on ≤480px (`w-full h-[100dvh]`), centred `max-w-md` on wider
 *   viewports, no horizontal scrolling between 320–480px.
 * - Every interactive control has `min-h-[44px]` and horizontal padding
 *   sufficient for a 44px-wide tap area.
 * - `aria-modal="true"`, Esc closes, and focus returns to the opener on
 *   close are inherited from the shared `Modal` component.
 * - Description textarea advertises an `aria-describedby` pointing at a
 *   live character-count element. Validation messages are collected in a
 *   single `role="alert"` container.
 *
 * Implements Requirements:
 *   2.2, 2.3, 2.4, 2.5, 2.6 (rider selection)
 *   3.1, 3.2, 3.3, 3.7     (incident type fixed dropdown)
 *   4.1, 4.3, 4.4, 4.5, 4.6 (description + validation)
 *   5.1, 5.2                (event association + in-form confirmation of event)
 *   6.1, 6.2, 6.3, 6.4, 6.5 (submission + feedback, cancel)
 *   7.1, 7.2, 7.3, 7.4      (mobile-first layout)
 *   13.4                    (client-side trimmed-length check mirroring server)
 *   17.11                   (capture flow behaves identically regardless of entry)
 *   18.16                   (min 44px touch targets inside the page context)
 *
 * See: design.md §Components and Interfaces → Component hierarchy (detail)
 *        → IncidentCaptureDialog; §Frontend Design → Mobile-first layout notes;
 *        §Accessibility Considerations.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { restClient, Rider } from '../../../api/rest-client';
import type {
  CreateIncidentRequest,
  Incident,
  IncidentType,
  UpdateIncidentRequest,
} from '../../../types/incidents';
import { Button } from '../../common/Button';
import { Modal } from '../../common/Modal';
import { useUIStore } from '../../../store/uiStore';
import { formatRiderName } from '../../../utils/riderDisplay';

const INCIDENT_TYPES: readonly IncidentType[] = ['Crash', 'Mechanical', 'Other'] as const;
const DESCRIPTION_MAX = 5000;

export type IncidentCaptureDialogMode = 'create' | 'edit';

/**
 * Minimum shape the dialog needs to prefill its three editable fields
 * plus the `incident_id` required by the PUT endpoint. Any extra fields
 * on an `Incident` are ignored so callers can pass the full object.
 */
export type IncidentInitialValues = Pick<
  Incident,
  'incident_id' | 'rider_id' | 'incident_type' | 'description'
>;

export interface IncidentCaptureDialogProps {
  /** The event the incident is being reported against. */
  eventId: string;
  /** Display name of the event, shown in the dialog for confirmation (Req 5.2). */
  eventName: string;
  /** ISO 8601 UTC start time of the event, shown formatted for confirmation (Req 5.2). */
  eventStartTime: string;
  /**
   * The roster to pick the involved rider from. The page owner
   * (Event_Incidents_Page per Req 2.1) is responsible for filtering to
   * active current-season riders before passing them in.
   */
  riders: Rider[];
  /** Whether the dialog is mounted/visible. */
  isOpen: boolean;
  /** Invoked whenever the dialog should close (Save success, Cancel, Esc, backdrop). */
  onClose: () => void;
  /** Fired once after a successful 2xx so the parent can refresh its list. */
  onSuccess: () => void;
  /**
   * Dialog mode — `'create'` (default) POSTs a new incident; `'edit'`
   * PUTs against an existing one. Edit mode requires `initialValues` to
   * carry the target `incident_id` plus the three editable fields.
   */
  mode?: IncidentCaptureDialogMode;
  /**
   * When `mode === 'edit'`, the initial values for the three editable
   * fields plus the target `incident_id`. Ignored in create mode.
   */
  initialValues?: IncidentInitialValues;
}

/**
 * Best-effort formatter for the event start time. Uses the same locale
 * options as `formatDateTimeForDisplay` in `utils/dateUtils.ts`; falls
 * back to the raw ISO string if parsing fails so a bad timestamp does not
 * blank the confirmation area (Req 5.2 — the user still sees something).
 */
function formatEventStartTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Derives a user-facing message from a thrown error after a failed POST.
 * Prefers the server-provided `error` field on the response body (Req 6.3);
 * falls back to a generic network message when the request never reached
 * the server (Req 6.4).
 */
function extractErrorMessage(err: unknown): string {
  // Axios-shaped error with a parsed JSON response
  const maybe = err as {
    response?: { data?: { error?: unknown; message?: unknown } };
    message?: unknown;
  };
  const serverError = maybe?.response?.data?.error;
  if (typeof serverError === 'string' && serverError.length > 0) {
    return serverError;
  }
  const serverMessage = maybe?.response?.data?.message;
  if (typeof serverMessage === 'string' && serverMessage.length > 0) {
    return serverMessage;
  }
  // If we never got a response, treat as a network failure (Req 6.4).
  if (!maybe?.response) {
    return 'Could not send submission';
  }
  if (typeof maybe?.message === 'string' && maybe.message.length > 0) {
    return maybe.message;
  }
  return 'Could not send submission';
}

export function IncidentCaptureDialog({
  eventId,
  eventName,
  eventStartTime,
  riders,
  isOpen,
  onClose,
  onSuccess,
  mode = 'create',
  initialValues,
}: IncidentCaptureDialogProps) {
  const showToast = useUIStore((state) => state.showToast);
  const isEditMode = mode === 'edit';

  // Form state (Req 5.2, 6.1). In edit mode, seed the three editable
  // fields from `initialValues`. Keep a copy of the initial values so we
  // can reset the form to them when the user cancels or on an Esc close.
  const [riderId, setRiderId] = useState<string>(
    () => (isEditMode && initialValues ? initialValues.rider_id : ''),
  );
  const [incidentType, setIncidentType] = useState<IncidentType>(
    () => (isEditMode && initialValues ? initialValues.incident_type : 'Crash'),
  );
  const [description, setDescription] = useState<string>(
    () => (isEditMode && initialValues ? initialValues.description : ''),
  );
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed state whenever the dialog is (re-)opened so editing a
  // different row after a previous open doesn't leak the previous row's
  // values. `isOpen` is the stable signal here — parents toggle it
  // between row selections.
  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode && initialValues) {
      setRiderId(initialValues.rider_id);
      setIncidentType(initialValues.incident_type);
      setDescription(initialValues.description);
    } else {
      setRiderId('');
      setIncidentType('Crash');
      setDescription('');
    }
    setSubmitting(false);
    setError(null);
    // We intentionally depend on `isOpen` only — re-running on every
    // initialValues identity change would clobber the user's in-progress
    // edits as the parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Roster partitioned so Students (anyone not typed 'Coach') appear before
  // Coaches, then alphabetized by `formatRiderName(rider)` within each group
  // using a case-insensitive `localeCompare` so the rendered label order
  // matches what the user actually sees (Req 2.3). Kept in a memo so we
  // don't re-sort on every keystroke of the description textarea.
  const sortedRiders = useMemo(() => {
    return [...riders].sort((a, b) => {
      const aIsCoach = a.type === 'Coach' ? 1 : 0;
      const bIsCoach = b.type === 'Coach' ? 1 : 0;
      if (aIsCoach !== bIsCoach) return aIsCoach - bIsCoach;
      return formatRiderName(a).localeCompare(formatRiderName(b), undefined, {
        sensitivity: 'base',
      });
    });
  }, [riders]);

  const trimmedLength = description.trim().length;

  // Derived validity (Req 2.5, 2.6, 3.4, 4.4, 4.5, 13.4).
  const riderValid = Boolean(riderId);
  const typeValid = (INCIDENT_TYPES as readonly string[]).includes(incidentType);
  const descriptionMissing = trimmedLength === 0;
  const descriptionTooLong = trimmedLength > DESCRIPTION_MAX;
  const descriptionValid = !descriptionMissing && !descriptionTooLong;
  const isValid = riderValid && typeValid && descriptionValid;

  const resetFormState = useCallback(() => {
    if (isEditMode && initialValues) {
      setRiderId(initialValues.rider_id);
      setIncidentType(initialValues.incident_type);
      setDescription(initialValues.description);
    } else {
      setRiderId('');
      setIncidentType('Crash');
      setDescription('');
    }
    setSubmitting(false);
    setError(null);
  }, [isEditMode, initialValues]);

  // Cancel: close + reset, NO network call (Req 6.5).
  const handleCancel = useCallback(() => {
    if (submitting) {
      // If a request is in flight, block cancel from racing state resets.
      // Re-using the shared Modal's onClose contract still means the parent
      // might try to close us; we simply ignore it until the POST resolves.
      return;
    }
    resetFormState();
    onClose();
  }, [submitting, resetFormState, onClose]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      if (!isValid || submitting) return;

      setSubmitting(true);
      setError(null);

      try {
        if (isEditMode) {
          if (!initialValues?.incident_id) {
            throw new Error(
              'IncidentCaptureDialog: edit mode requires initialValues.incident_id',
            );
          }
          const payload: UpdateIncidentRequest = {
            incident_id: initialValues.incident_id,
            rider_id: riderId,
            incident_type: incidentType,
            description: description.trim(),
          };
          await restClient.updateIncident(payload);
          // Success toast distinguishes edit from create so the user
          // has unambiguous feedback about which action completed.
          const selectedRider = riders.find((r) => r.rider_id === riderId) ?? null;
          const riderLabel = formatRiderName(selectedRider) || 'the selected rider';
          showToast(
            'success',
            `Incident updated for ${riderLabel} — ${incidentType}`,
          );
        } else {
          const payload: CreateIncidentRequest = {
            event_id: eventId,
            rider_id: riderId,
            incident_type: incidentType,
            description: description.trim(),
          };
          await restClient.createIncident(payload);
          const selectedRider = riders.find((r) => r.rider_id === riderId) ?? null;
          const riderLabel = formatRiderName(selectedRider) || 'the selected rider';
          showToast(
            'success',
            `Incident saved for ${riderLabel} — ${incidentType}`,
          );
        }
        onSuccess();
        resetFormState();
        onClose();
      } catch (err) {
        // Non-2xx or network failure (Req 6.3, 6.4).
        // eslint-disable-next-line no-console
        console.error('[IncidentCaptureDialog] Failed to save incident', err);
        setError(extractErrorMessage(err));
        setSubmitting(false);
      }
    },
    [
      isValid,
      submitting,
      isEditMode,
      initialValues,
      eventId,
      riderId,
      incidentType,
      description,
      riders,
      showToast,
      onSuccess,
      resetFormState,
      onClose,
    ],
  );

  // Collected validation messages for the single role="alert" container
  // (Req 2.6, 4.4, 4.5, 5.3/accessibility).
  const validationMessages: string[] = [];
  if (!riderValid) {
    validationMessages.push('Please select a rider.');
  }
  if (descriptionMissing) {
    validationMessages.push('Description is required.');
  }
  if (descriptionTooLong) {
    validationMessages.push(
      `Description must be ${DESCRIPTION_MAX} characters or fewer (currently ${trimmedLength}).`,
    );
  }

  const descCountId = 'incident-description-count';
  const validationAlertId = 'incident-validation-messages';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={isEditMode ? 'Edit Incident' : 'Report Incident'}
      // Full-screen on mobile (the Modal already does this by default), with
      // a narrower centred shell on ≥sm viewports (Req 7.1).
      className="sm:max-w-md"
      showCloseButton={!submitting}
    >
      <form
        onSubmit={handleSubmit}
        noValidate
        // A `space-y-*` utility gives breathing room between controls so
        // Save and Cancel remain visible without scrolling on a 667px-tall
        // viewport when the Incident Type select is focused (Req 7.2).
        className="space-y-4"
      >
        {/* Event context — Req 5.2 */}
        <div className="rounded-md bg-primary-50 border border-primary-200 p-3">
          <p className="text-xs uppercase tracking-wide text-primary-700 font-medium">
            Reporting against
          </p>
          <p className="text-sm font-semibold text-primary-900 break-words">{eventName}</p>
          <p className="text-sm text-primary-700">{formatEventStartTime(eventStartTime)}</p>
        </div>

        {/* Rider selector — Req 2.2, 2.3, 2.5 */}
        <div>
          <label
            htmlFor="incident-rider"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Rider <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <select
            id="incident-rider"
            name="rider_id"
            value={riderId}
            onChange={(e) => setRiderId(e.target.value)}
            disabled={submitting}
            required
            aria-required="true"
            aria-invalid={!riderValid}
            className="w-full min-h-[44px] px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">Select Rider...</option>
            {sortedRiders.map((rider) => (
              <option key={rider.rider_id} value={rider.rider_id}>
                {formatRiderName(rider)}
              </option>
            ))}
          </select>
        </div>

        {/* Incident Type — Req 3.1, 3.2, 3.3, 3.7 */}
        <div>
          <label
            htmlFor="incident-type"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Incident Type <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <select
            id="incident-type"
            name="incident_type"
            value={incidentType}
            onChange={(e) => setIncidentType(e.target.value as IncidentType)}
            disabled={submitting}
            required
            aria-required="true"
            className="w-full min-h-[44px] px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            {INCIDENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {/* Description — Req 4.1, 4.4, 4.5, 4.6 */}
        <div>
          <label
            htmlFor="incident-description"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Description <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <textarea
            id="incident-description"
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            disabled={submitting}
            required
            aria-required="true"
            aria-invalid={!descriptionValid}
            aria-describedby={descCountId}
            className="w-full min-h-[44px] px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <p
            id={descCountId}
            className={`mt-1 text-xs ${
              descriptionTooLong ? 'text-red-600' : 'text-gray-500'
            }`}
          >
            {trimmedLength}/{DESCRIPTION_MAX} characters
          </p>
        </div>

        {/* Validation messages — single role="alert" container (a11y). */}
        <div
          id={validationAlertId}
          role="alert"
          aria-live="polite"
          className={validationMessages.length > 0 ? 'text-sm text-red-600' : 'sr-only'}
        >
          {validationMessages.length > 0 && (
            <ul className="list-disc list-inside space-y-0.5">
              {validationMessages.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          )}
        </div>

        {/* Server / network error — Req 6.3, 6.4 */}
        {error && (
          <div
            role="alert"
            aria-live="assertive"
            className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        {/* Action row — Save + Cancel (Req 6.1, 6.5, 7.2, 7.3) */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={handleCancel}
            disabled={submitting}
            className="min-h-[44px] px-6"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isValid || submitting}
            className="min-h-[44px] px-6"
            aria-disabled={!isValid || submitting}
          >
            {submitting ? 'Saving…' : isEditMode ? 'Save changes' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default IncidentCaptureDialog;
